import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type BTCPeriod = '1Y' | '2Y' | '3Y' | '5Y' | '10Y';

export interface BTCHistoryPoint {
  date: string;
  price: number;
}

async function fetchBTCHistory(period: BTCPeriod, forceRefresh = false): Promise<BTCHistoryPoint[]> {
  console.log(`[useBTCHistory] Fetching BTC history for period: ${period}, forceRefresh: ${forceRefresh}`);
  
  const headers: Record<string, string> = {};
  if (forceRefresh) {
    headers['x-force-refresh'] = '1';
  }
  
  const { data, error } = await supabase.functions.invoke('btc-history', {
    body: { period, forceRefresh },
    headers,
  });

  if (error) {
    console.error('[useBTCHistory] Error fetching BTC history:', error);
    throw error;
  }

  if (!data || !Array.isArray(data)) {
    console.error('[useBTCHistory] Invalid response:', data);
    throw new Error('Invalid response from btc-history');
  }

  console.log(`[useBTCHistory] Received ${data.length} points, range: ${data[0]?.date} to ${data[data.length - 1]?.date}`);
  
  return data as BTCHistoryPoint[];
}

export function useBTCHistory(period: BTCPeriod = '2Y') {
  const queryClient = useQueryClient();
  
  const query = useQuery({
    queryKey: ['btc-history', period],
    queryFn: () => fetchBTCHistory(period, false),
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 2 * 60 * 60 * 1000, // 2 hours
    retry: 2,
    refetchOnWindowFocus: false,
  });

  const forceRefresh = async () => {
    console.log(`[useBTCHistory] Force refreshing BTC history for period: ${period}`);
    const freshData = await fetchBTCHistory(period, true);
    queryClient.setQueryData(['btc-history', period], freshData);
    return freshData;
  };

  return {
    ...query,
    forceRefresh,
  };
}
